import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft } from "lucide-react";
import LanguageSwitcher from "../../components/LanguageSwitcher";

/**
 * Shared shell for legal / policy pages (Terms, Privacy, Security).
 * Keeps the same warm neutral palette + coral accent as the login screen so
 * users don't feel like they've bounced to a different app.
 *
 * Props:
 *   title     — page title (e.g. "Terms of Service")
 *   updated   — ISO date string shown under the title
 *   children  — the body content (headings + paragraphs)
 */
export default function LegalPage({ title, updated, children }) {
  const { t } = useTranslation(["common", "legal"]);
  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <header className="max-w-5xl mx-auto px-6 sm:px-10 pt-8 pb-4 flex items-center justify-between">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-[#7A7571] hover:text-[#2C2A29] transition-colors"
          data-testid="legal-back"
        >
          <ArrowLeft size={16} /> {t("common:actions.backToSignIn")}
        </Link>
        <div className="flex items-center gap-3">
          <LanguageSwitcher variant="header" />
          <div className="flex items-center gap-2">
            <img src="/logo-mark.png" alt="OrkaiFlow" className="w-8 h-8 object-contain" />
            <span className="font-display text-base font-bold text-[#2C2A29]">OrkaiFlow</span>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 sm:px-10 py-10">
        <h1 className="font-display text-4xl sm:text-5xl font-bold text-[#2C2A29] tracking-tight leading-tight [text-wrap:balance]">
          {title}
        </h1>
        {updated && (
          <p className="text-sm text-[#7A7571] mt-3">{t("legal:shell.lastUpdated", { date: updated })}</p>
        )}
        <div className="mt-8 space-y-6 text-[#2C2A29] leading-relaxed text-base [text-wrap:pretty]">
          {children}
        </div>
        <footer className="mt-16 pt-8 border-t border-[#EBE5D9] text-xs text-[#7A7571] flex flex-wrap gap-x-4 gap-y-1">
          <span>{t("common:footer.copyright")}</span>
          <Link to="/terms" className="hover:text-[#2C2A29]">{t("common:footer.terms")}</Link>
          <Link to="/privacy" className="hover:text-[#2C2A29]">{t("common:footer.privacy")}</Link>
          <Link to="/security" className="hover:text-[#2C2A29]">{t("common:footer.security")}</Link>
          <a href="mailto:support@orkaiflow.com" className="hover:text-[#2C2A29]">{t("common:footer.support")}</a>
        </footer>
      </main>
    </div>
  );
}

/** Small typographic helpers so every legal page reads the same. */
export function Section({ heading, children }) {
  return (
    <section>
      <h2 className="font-display text-xl sm:text-2xl font-semibold text-[#2C2A29] mb-2 tracking-tight">
        {heading}
      </h2>
      <div className="space-y-3 text-[#4B4642]">{children}</div>
    </section>
  );
}
